import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { Mail, Phone, MapPin, Send, Clock, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const ContactPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast({
        title: "Message Sent!",
        description: "We'll get back to you as soon as possible.",
      });
    } catch {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen bg-slate-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-12 text-center max-w-md shadow-xl"
          data-testid="contact-success"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#0A2342] mb-4 font-['Playfair_Display']">
            Message Received!
          </h2>
          <p className="text-slate-600 mb-6">
            Thanks for reaching out! We typically respond within a few hours during business hours.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({ name: "", email: "", phone: "", message: "" });
            }}
            className="btn-primary"
            data-testid="contact-new-message"
          >
            Send Another Message
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-[#0A2342] py-16 md:py-24">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center px-4 py-2 bg-[#DE6E4B] rounded-full text-white text-sm font-medium mb-6">
              <Mail className="w-4 h-4 mr-2" />
              Get In Touch
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 font-['Playfair_Display']">
              Contact Us
            </h1>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Have questions? Need help finding your perfect Houston home? We're here to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="container-main">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="text-2xl font-bold text-[#0A2342] mb-6 font-['Playfair_Display']">
                Let's Talk About Your Next Home
              </h2>
              <p className="text-slate-600 mb-8">
                Whether you're looking to rent, buy, or invest in the Houston area, we're here to guide you every step of the way. Our service is completely free for renters!
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#DE6E4B]/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#DE6E4B]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A2342] mb-1">Email</h3>
                    <a href="mailto:info@urbannestquest.com" className="text-slate-600 hover:text-[#DE6E4B] transition-colors">
                      info@urbannestquest.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#DE6E4B]/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#DE6E4B]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A2342] mb-1">Phone</h3>
                    <a href="tel:+19362241484" className="text-slate-600 hover:text-[#DE6E4B] transition-colors">
                      (936) 224-1484
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#DE6E4B]/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#DE6E4B]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A2342] mb-1">Service Areas</h3>
                    <p className="text-slate-600">
                      Houston, Katy, Sugar Land, The Woodlands, Conroe
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#DE6E4B]/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#DE6E4B]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A2342] mb-1">Response Time</h3>
                    <p className="text-slate-600">
                      We typically respond within a few hours
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="bg-slate-50 rounded-2xl p-8" data-testid="contact-form">
                <h3 className="text-xl font-bold text-[#0A2342] mb-6 font-['Playfair_Display']">
                  Send Us a Message
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-field"
                      placeholder="Your name"
                      data-testid="contact-form-name"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-field"
                      placeholder="your@email.com"
                      data-testid="contact-form-email"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Phone (Optional)</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="input-field"
                      placeholder="(555) 123-4567"
                      data-testid="contact-form-phone"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Message *</label>
                    <textarea
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="textarea-field"
                      rows={5}
                      placeholder="How can we help you?"
                      data-testid="contact-form-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full flex items-center justify-center"
                    data-testid="contact-form-submit"
                  >
                    {loading ? (
                      <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    ) : (
                      <>Send Message <Send className="w-5 h-5 ml-2" /></>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
